import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '@/lib/apiAuth';
import { supabaseAdmin } from '@/lib/supabaseAdmin';
import type { InvoiceSerieCreateInput } from '@/lib/invoicing/types';

// =============================================
// Handler
// =============================================

export default withAuth(async (req, res, _user) => {
  try {
    switch (req.method) {
      case 'GET':
        return handleGet(req, res);
      case 'POST':
        return handleCreate(req, res);
      case 'PUT':
        return handleUpdate(req, res);
      default:
        return res.status(405).json({ error: 'Método no permitido' });
    }
  } catch (error: any) {
    console.error('[API invoice-series] Unexpected error:', error);
    return res.status(500).json({ error: error.message || 'Error interno del servidor' });
  }
});

// =============================================
// GET: List series for an empresa
// =============================================

async function handleGet(req: NextApiRequest, res: NextApiResponse) {
  const { empresa_id, activa } = req.query;

  if (!empresa_id) {
    return res.status(400).json({ error: 'empresa_id es requerido' });
  }

  let query = supabaseAdmin
    .from('invoice_series')
    .select('*')
    .eq('empresa_id', empresa_id as string)
    .order('codigo', { ascending: true });

  if (activa !== undefined) {
    query = query.eq('activa', activa === 'true');
  }

  const { data, error } = await query;

  if (error) {
    console.error('[API invoice-series] GET error:', error);
    return res.status(500).json({ error: `Error al listar series: ${error.message}` });
  }

  return res.status(200).json({ data: data ?? [] });
}

// =============================================
// POST: Create a new invoice series
// =============================================

async function handleCreate(req: NextApiRequest, res: NextApiResponse) {
  const body: InvoiceSerieCreateInput = req.body;

  if (!body.empresa_id) return res.status(400).json({ error: 'empresa_id es requerido' });
  if (!body.codigo) return res.status(400).json({ error: 'codigo es requerido' });
  if (!body.nombre) return res.status(400).json({ error: 'nombre es requerido' });
  if (!body.tipo) return res.status(400).json({ error: 'tipo es requerido' });

  // Check for duplicate codigo within empresa
  const { data: existing } = await supabaseAdmin
    .from('invoice_series')
    .select('id')
    .eq('empresa_id', body.empresa_id)
    .eq('codigo', body.codigo)
    .limit(1);

  if (existing && existing.length > 0) {
    return res.status(409).json({ error: `Ya existe una serie con código '${body.codigo}' para esta empresa` });
  }

  const serieData = {
    empresa_id: body.empresa_id,
    codigo: body.codigo,
    nombre: body.nombre,
    tipo: body.tipo,
    prefijo: body.prefijo ?? null,
    siguiente_numero: body.siguiente_numero ?? 1,
    formato_numero: body.formato_numero ?? '{prefijo}{numero:05d}',
    activa: body.activa ?? true,
    verifactu_habilitado: body.verifactu_habilitado ?? false,
  };

  const { data, error } = await supabaseAdmin
    .from('invoice_series')
    .insert(serieData)
    .select()
    .single();

  if (error) {
    console.error('[API invoice-series] POST error:', error);
    return res.status(500).json({ error: `Error al crear serie: ${error.message}` });
  }

  return res.status(201).json({ success: true, data });
}

// =============================================
// PUT: Update an invoice series
// =============================================

async function handleUpdate(req: NextApiRequest, res: NextApiResponse) {
  const { id, ...updates } = req.body as Partial<InvoiceSerieCreateInput> & { id: string };

  if (!id) return res.status(400).json({ error: 'id es requerido' });

  // Verify series exists
  const { data: existing, error: fetchError } = await supabaseAdmin
    .from('invoice_series')
    .select('id')
    .eq('id', id)
    .single();

  if (fetchError || !existing) {
    return res.status(404).json({ error: 'Serie no encontrada' });
  }

  // Build update data
  const updateData: Record<string, unknown> = {};
  const allowedFields = [
    'nombre', 'tipo', 'prefijo', 'siguiente_numero', 'formato_numero',
    'activa', 'verifactu_habilitado',
  ];

  for (const field of allowedFields) {
    if ((updates as Record<string, unknown>)[field] !== undefined) {
      updateData[field] = (updates as Record<string, unknown>)[field];
    }
  }

  // Don't allow changing codigo if invoices exist with this series
  if (updates.codigo !== undefined) {
    const { data: invoicesUsing } = await supabaseAdmin
      .from('invoices')
      .select('id')
      .eq('serie_id', id)
      .limit(1);

    if (invoicesUsing && invoicesUsing.length > 0) {
      return res.status(400).json({ error: 'No se puede cambiar el código de una serie que ya tiene facturas' });
    }
    updateData.codigo = updates.codigo;
  }

  if (Object.keys(updateData).length === 0) {
    return res.status(400).json({ error: 'No se proporcionaron campos para actualizar' });
  }

  const { data, error } = await supabaseAdmin
    .from('invoice_series')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('[API invoice-series] PUT error:', error);
    return res.status(500).json({ error: `Error al actualizar serie: ${error.message}` });
  }

  return res.status(200).json({ success: true, data });
}
